import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiPortfolioSummary, ApiPortfolioDetail } from '@/types';
import apiService from '@/services/apiService';
import LCIcon from '@/components/common/LCIcon';
import { fmtCurrency, fmtPct, fmtNumber, LCBtn, LCChip, LCSortHeader, CLS } from '@/components/common/LCPrimitives';

type View = 'security' | 'type';
type SortField = 'name' | 'total_mv' | 'total_qty' | 'holders' | 'pct' | 'portfolio' | 'client'| 'rm' | 'qty' | 'mkt_val' | 'weight' | 'margin_req' | 'lc_req' | 'risk_contrib';
type SortDir = 'asc' | 'desc';
type ConcentrationRisk = 'LOW' | 'MEDIUM' | 'HIGH';

interface SecurityExposure {
  cusip: string;
  ticker: string | null;
  name: string;
  type: string | null;
  total_mv: number;
  total_qty: number;
  total_margin_req: number;
  total_lc_req: number;
  risk_contribution: number;
  concentration_risk: ConcentrationRisk;
  holders: Array<{
    portfolio_id: string;
    client: string;
    rm: string | null;
    quantity: number | null;
    market_value: number | null;
    weight: number | null;
  }>;
}

interface TypeExposure {
  type: string;
  mv: number;
  securities: number;
  holders: number;
}

const ExposureAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const [view, setView] = useState<View>('security');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('total_mv');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [holderSortField, setHolderSortField] = useState<SortField>('mkt_val');
  const [holderSortDir, setHolderSortDir] = useState<SortDir>('desc');

  const [portfoliosData, setPortfoliosData] = useState<ApiPortfolioSummary[]>([]);
  const [portfolioDetails, setPortfolioDetails] = useState<Map<string, ApiPortfolioDetail>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [portfolios, details] = await Promise.all([
        apiService.getPortfolios(),
        apiService.getAllPortfolioDetails(),
      ]);
      setPortfoliosData(portfolios);
      const detailsMap = new Map<string, ApiPortfolioDetail>();
      details.forEach(d => detailsMap.set(d.portfolio_id_lig, d));
      setPortfolioDetails(detailsMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const bySecurity = useMemo<SecurityExposure[]>(() => {
    const totalAUM = portfoliosData.reduce((s, p) => s + (p.cash_account_equity || 0) + (p.margin_account_equity || 0), 0);

    const map: Record<string, SecurityExposure> = {};
    portfolioDetails.forEach((detail, portfolioId) => {
      const summary = portfoliosData.find(p => p.portfolio_id_lig === portfolioId);
      detail.positions.forEach(pos => {
        const k = pos.cusip || pos.security_name;
        if (!map[k]) map[k] = {
          cusip: pos.cusip, ticker: pos.ticker, name: pos.security_name,
          type: pos.security_type, total_mv: 0, total_qty: 0,
          total_margin_req: 0, total_lc_req: 0, risk_contribution: 0,
          concentration_risk: 'LOW', holders: [],
        };
        map[k].total_mv += pos.market_value || 0;
        map[k].total_qty += pos.quantity || 0;
        map[k].total_margin_req += pos.pershing_requirement || 0;
        map[k].total_lc_req += pos.position_lc_margin_requirement || 0;
        map[k].holders.push({
          portfolio_id: portfolioId,
          client: summary?.client_name || 'Unknown',
          rm: summary?.rm_name || null,
          quantity: pos.quantity,
          market_value: pos.market_value,
          weight: pos.weight,
        });
      });
    });

    return Object.values(map).map(s => {
      const weight = totalAUM > 0 ? (s.total_mv / totalAUM) * 100 : 0;
      s.risk_contribution = totalAUM > 0 ? (s.total_lc_req / totalAUM) * 100 : 0;
      s.concentration_risk = (weight > 5 || s.holders.length > 3) ? 'HIGH'
        : (weight > 2 || s.holders.length > 2) ? 'MEDIUM' : 'LOW';
      return s;
    }).sort((a, b) => b.total_mv - a.total_mv);
  }, [portfoliosData, portfolioDetails]);

  const byType = useMemo<TypeExposure[]>(() => {
    const map: Record<string, TypeExposure> = {};
    bySecurity.forEach(s => {
      const t = s.type || 'OTHER';
      if (!map[t]) map[t] = { type: t, mv: 0, securities: 0, holders: 0 };
      map[t].mv += s.total_mv;
      map[t].securities++;
      map[t].holders += s.holders.length;
    });
    return Object.values(map).sort((a, b) => b.mv - a.mv);
  }, [bySecurity]);

  


  const totalMV = bySecurity.reduce((s, x) => s + x.total_mv, 0);

  const onSort = (f: string) => {
    const field = f as SortField;
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const onHolderSort = (f: string) => {
    const field = f as SortField;
    if (holderSortField === field) setHolderSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setHolderSortField(field); setHolderSortDir('desc'); }
  };

  const sortHolders = (holders: SecurityExposure['holders']) => {
    return [...holders].sort((a, b) => {
      let av: any, bv: any;
      switch (holderSortField) {
        case 'portfolio': av = a.portfolio_id; bv = b.portfolio_id; break;
        case 'client':    av = a.client;       bv = b.client;       break;
        case 'rm':        av = a.rm ?? '';     bv = b.rm ?? '';     break;
        case 'qty':       av = a.quantity ?? 0;      bv = b.quantity ?? 0;      break;
        case 'mkt_val':   av = a.market_value ?? 0;  bv = b.market_value ?? 0;  break;
        case 'weight':    av = a.weight ?? 0;        bv = b.weight ?? 0;        break;
        default:          return 0;
      }
      if (av < bv) return holderSortDir === 'asc' ? -1 : 1;
      if (av > bv) return holderSortDir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const filtered = useMemo(() => {
    let arr = bySecurity.filter(s => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (s.name || '').toLowerCase().includes(q) ||
        (s.cusip || '').toLowerCase().includes(q) ||
        (s.ticker || '').toLowerCase().includes(q);
    });
    arr.sort((a, b) => {
      let av: any, bv: any;
      switch (sortField) {
        case 'name':        av = a.name;              bv = b.name;              break;
        case 'total_mv':    av = a.total_mv;           bv = b.total_mv;          break;
        case 'total_qty':   av = a.total_qty;          bv = b.total_qty;         break;
        case 'holders':     av = a.holders.length;     bv = b.holders.length;    break;
        case 'pct':         av = a.total_mv;           bv = b.total_mv;          break;
        case 'margin_req':  av = a.total_margin_req;   bv = b.total_margin_req;  break;
        case 'lc_req':      av = a.total_lc_req;       bv = b.total_lc_req;      break;
        case 'risk_contrib':av = a.risk_contribution;  bv = b.risk_contribution; break;
        default: av = 0; bv = 0;
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [bySecurity, search, sortField, sortDir]);

  const toggle = (key: string) => {
    setExpanded(prev => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  };

  if (loading) return (
    <div className={`${CLS.page} flex items-center justify-center min-h-[400px] flex-col gap-3`}>
      <span className={CLS.muted}>Loading exposure data… This may take a moment.</span>
    </div>
  );

  if (error) return (
    <div className={CLS.page}>
      <div className="bg-[rgba(229,0,37,0.06)] border border-[#E50025] px-[22px] py-[18px] mb-4">
        <div className="font-bold text-[#E50025] text-[12px] tracking-[0.12em] uppercase mb-1.5">Error</div>
        <div style={{ fontSize: 13 }}>{error}</div>
      </div>
      <LCBtn variant="primary" size="sm" onClick={fetchData}><LCIcon name="refresh" size={12} /> Retry</LCBtn>
    </div>
  );

  return (
    <div className={CLS.page}>
      <div className={CLS.pageHead}>
        <div>
          <h1 className={CLS.pageH1}>Exposure Analysis</h1>
          <div className={CLS.pageSub}>Cross-portfolio exposure by security or asset class — drill in to see every holder.</div>
        </div>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap gap-3 mb-[18px]">
        <div className="bg-white border border-black/[0.06] border-t-2 border-t-[#E50025] p-[12px_14px] flex flex-col relative flex-[1_1_140px] min-w-0">
          <div className="text-[9.5px] font-bold tracking-[0.14em] uppercase text-[#7A7A76]">Total Exposure</div>
          <div className="font-serif font-normal text-[22px] leading-tight mt-1.5 tracking-[-0.01em] [font-feature-settings:'tnum']">{fmtCurrency(totalMV)}</div>
          <div className="mt-1.5 text-[10.5px] text-[#7A7A76]">across {bySecurity.length} securities</div>
        </div>
        {byType.slice(0, 3).map(t => (
          <div className="bg-white border border-black/[0.06] p-[12px_14px] flex flex-col relative flex-[1_1_140px] min-w-0" key={t.type}>
            <div className="text-[9.5px] font-bold tracking-[0.14em] uppercase text-[#7A7A76]">{t.type}</div>
            <div className="font-serif font-normal text-[22px] leading-tight mt-1.5 tracking-[-0.01em] [font-feature-settings:'tnum']">{fmtCurrency(t.mv)}</div>
            <div className="mt-1.5 text-[10.5px] text-[#7A7A76]">{t.securities} securities · {fmtPct(totalMV > 0 ? t.mv / totalMV : 0, 1)} of total</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-black/10 mb-[22px]">
        {(['security', 'type'] as View[]).map((v, i) => (
          <button
            key={v}
            className={`bg-transparent border-0 px-[18px] py-3 text-[12px] font-semibold tracking-[0.04em] cursor-pointer relative inline-flex items-center gap-2 transition-colors ${view === v ? 'text-[#E50025] after:absolute after:content-[\'\'] after:left-3 after:right-3 after:-bottom-px after:h-0.5 after:bg-[#E50025]' : 'text-[#333] hover:text-black'}`}
            onClick={() => setView(v)}
          >
            {v === 'security' ? 'By Security' : 'By Asset Class'}
          </button>
        ))}
      </div>

      {view === 'security' && (
        <>
          <div className={`${CLS.surface} p-[14px_16px] mb-3.5`}>
            <div className={CLS.field}>
              <label className={CLS.fieldLabel}>Search</label>
              <div className={CLS.inputWrap}>
                <LCIcon name="search" size={14} className={CLS.inputIco} />
                <input className={`${CLS.input} pl-[38px]`} placeholder="Security, CUSIP, ticker…" value={search} onChange={e => setSearch(e.target.value)} />
              </div>
            </div>
          </div>

          <div className={CLS.tableWrap}>
            <table className={CLS.table}>
              <thead>
                <tr>
                  <th className={CLS.th} style={{ width: 110 }}>CUSIP</th>
                  <LCSortHeader label="Security" field="name" active={sortField === 'name'} dir={sortDir} onClick={onSort} />
                  <th className={CLS.th}>Type</th>
                  <LCSortHeader label="Total Exposure" field="total_mv" active={sortField === 'total_mv'} dir={sortDir} onClick={onSort} className={CLS.tdNum} />
                  <LCSortHeader label="Total Weight" field="pct" active={sortField === 'pct'} dir={sortDir} onClick={onSort} className={CLS.tdNum} />
                  <LCSortHeader label="Pershing Req" field="margin_req" active={sortField === 'margin_req'} dir={sortDir} onClick={onSort} className={CLS.tdNum} />
                  <LCSortHeader label="LC Req" field="lc_req" active={sortField === 'lc_req'} dir={sortDir} onClick={onSort} className={CLS.tdNum} />
                  <LCSortHeader label="Risk Contrib" field="risk_contrib" active={sortField === 'risk_contrib'} dir={sortDir} onClick={onSort} className={CLS.tdNum} />
                  <th className={CLS.th}>Conc. Risk</th>
                  <LCSortHeader label="Portfolios" field="holders" active={sortField === 'holders'} dir={sortDir} onClick={onSort} className={CLS.tdNum} />
                  <th className={CLS.th} style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => {
                  const key = s.cusip || s.name;
                  const isExpanded = expanded.has(key);
                  return (
                    <React.Fragment key={key}>
                      <tr className="group cursor-pointer transition-colors hover:bg-[rgba(229,0,37,0.04)]" onClick={() => toggle(key)}>
                        <td className={`${CLS.td} ${CLS.tdMono}`}>{s.cusip || '—'}</td>
                        <td className={CLS.td}>
                          <div className="font-medium">{s.name}</div>
                          {s.ticker && <div className={`${CLS.tdMono} text-[10px] ${CLS.muted}`}>{s.ticker}</div>}
                        </td>
                        <td className={CLS.td}><LCChip variant="outline" label={s.type || '—'} /></td>
                        <td className={`${CLS.td} ${CLS.tdNum} font-semibold`}>{fmtCurrency(s.total_mv)}</td>
                        <td className={`${CLS.td} ${CLS.tdNum}`}>{totalMV > 0 ? fmtPct(s.total_mv / totalMV, 2) : '—'}</td>
                        <td className={`${CLS.td} ${CLS.tdNum}`}>{fmtCurrency(s.total_margin_req)}</td>
                        <td className={`${CLS.td} ${CLS.tdNum}`}>{fmtCurrency(s.total_lc_req)}</td>
                        <td className={`${CLS.td} ${CLS.tdNum}`}>{fmtPct(s.risk_contribution / 100, 2)}</td>
                        <td className={CLS.td}>
                          <span className={`inline-flex items-center px-[7px] py-[2px] text-[9.5px] font-bold tracking-[0.06em] uppercase text-white ${s.concentration_risk === 'HIGH' ? 'bg-[#E50025]' : s.concentration_risk === 'MEDIUM' ? 'bg-[#F5A623]' : 'bg-[#1f8a4a]'}`}>
                            {s.concentration_risk}
                          </span>
                        </td>
                        <td className={`${CLS.td} ${CLS.tdNum}`}>{s.holders.length}</td>
                        <td className={CLS.td}>
                          <LCIcon name="chevron" size={14} className={`${CLS.muted} block transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-[#FAF9F8]">
                          <td colSpan={11} className="px-[22px] py-4">
                            <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-[#7A7A76] mb-2">Portfolios ({s.holders.length})</div>
                            <table className={`${CLS.table} bg-transparent`}>
                              <thead>
                                <tr style={{ background: 'transparent' }}>
                                  <LCSortHeader label="Portfolio"    field="portfolio" active={holderSortField === 'portfolio'} dir={holderSortDir} onClick={onHolderSort} />
                                  <LCSortHeader label="Client"       field="client"    active={holderSortField === 'client'}    dir={holderSortDir} onClick={onHolderSort} />
                                  <LCSortHeader label="RM"           field="rm"        active={holderSortField === 'rm'}        dir={holderSortDir} onClick={onHolderSort} />
                                  <LCSortHeader label="Quantity"     field="qty"       active={holderSortField === 'qty'}       dir={holderSortDir} onClick={onHolderSort} className={CLS.tdNum} />
                                  <LCSortHeader label="Market Value" field="mkt_val"   active={holderSortField === 'mkt_val'}   dir={holderSortDir} onClick={onHolderSort} className={CLS.tdNum} />
                                  <LCSortHeader label="Weight"       field="weight"    active={holderSortField === 'weight'}    dir={holderSortDir} onClick={onHolderSort} className={CLS.tdNum} />
                                </tr>
                              </thead>
                              <tbody>
                                {sortHolders(s.holders).map((h, i) => (
                                  <tr key={i} className="group cursor-pointer transition-colors hover:bg-[rgba(229,0,37,0.04)]" onClick={e => { e.stopPropagation(); navigate(`/portfolio/${h.portfolio_id}`); }}>
                                    <td className={`${CLS.td} ${CLS.tdMono}`}>{h.portfolio_id}</td>
                                    <td className={`${CLS.td} ${CLS.tdClient}`}>{h.client}</td>
                                    <td className={CLS.td}>{h.rm || '—'}</td>
                                    <td className={`${CLS.td} ${CLS.tdNum}`}>{fmtNumber(h.quantity)}</td>
                                    <td className={`${CLS.td} ${CLS.tdNum}`}>{fmtCurrency(h.market_value)}</td>
                                    <td className={`${CLS.td} ${CLS.tdNum}`}>{h.weight != null ? `${(h.weight * 100).toFixed(2)}%` : '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={11} className={`${CLS.td} ${CLS.muted}`} style={{ padding: 30, textAlign: 'center' }}>No securities match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {view === 'type' && (
        <div className={CLS.tableWrap}>
          <table className={CLS.table}>
            <thead>
              <tr>
                <th className={CLS.th}>Asset Class</th>
                <th className={CLS.th}>Distribution</th>
                <th className={`${CLS.th} ${CLS.tdNum}`}>Total Exposure</th>
                <th className={`${CLS.th} ${CLS.tdNum}`}>Securities</th>
                <th className={`${CLS.th} ${CLS.tdNum}`}>Holdings</th>
                <th className={`${CLS.th} ${CLS.tdNum}`}>% of Book</th>
              </tr>
            </thead>
            <tbody>
              {byType.map(t => (
                <tr key={t.type} className="transition-colors hover:bg-[#FAF9F8]">
                  <td className={CLS.td}><LCChip variant="outline" label={t.type} /></td>
                  <td className={CLS.td}>
                    <div className="h-2 bg-black/[0.06] w-[280px] relative">
                      <div className="absolute inset-0 bg-[#E50025]" style={{ width: `${totalMV > 0 ? (t.mv / totalMV) * 100 : 0}%` }} />
                    </div>
                  </td>
                  <td className={`${CLS.td} ${CLS.tdNum} font-semibold`}>{fmtCurrency(t.mv)}</td>
                  <td className={`${CLS.td} ${CLS.tdNum}`}>{t.securities}</td>
                  <td className={`${CLS.td} ${CLS.tdNum}`}>{t.holders}</td>
                  <td className={`${CLS.td} ${CLS.tdNum}`}>{totalMV > 0 ? fmtPct(t.mv / totalMV, 2) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ExposureAnalysis;
